import { html, nothing, TemplateResult } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { WEB_COMPONENT_HEALTH_DASHBOARD_ENDPOINT_ENCRYPTION } from '../../../../webcomponents.constants';
import bb from 'billboard.js/dist/billboard.pkgd';
import { styles } from '../view/styles';
import { translateFn } from '../../../../utils/translator.helper';
import { GzGenericWidgetContent } from '../../../../genericDashboard/gzGenericWidget';
import { EndpointEncryptionItem, EndpointEncryptionPayload, EndpointEncryptionStringsSection } from '../types';
import { WidgetStateFrontEnd } from '../../../../genericDashboard/gzGenericWidget/types';
import { getTranslatedWidgetTitle } from '../../../../genericDashboard/utils/translations';
import { removeElementClass, setElementClass } from '../../../../utils/htmlElement.utils';
import {
  HealthDashboardEndpointEncryptionData,
  HealthDashboardInfoStateData,
} from '@nirvana/shared/dtos/types/widgets/common';

@customElement(WEB_COMPONENT_HEALTH_DASHBOARD_ENDPOINT_ENCRYPTION)
export class GzHealthDashboardEndpointEncryptionWebComponent extends GzGenericWidgetContent {
  static get styles() {
    return styles;
  }

  @property({ type: Object, attribute: true, reflect: true })
  payload: { endpointsEncryption: EndpointEncryptionPayload };

  @query('#chart-content')
  private chartContent: HTMLElement;

  @query('#healthDashboardEndpointEncryption')
  private endpointEncryptionContainer: HTMLElement;

  @query('#chart-legend-container')
  private sectionContent: HTMLElement;

  private verticalPadding: number;

  changedInfoBoxText = `
    <gz-tooltip
      isEllipsis
      class="two-line-tooltip"
      tooltipText="${translateFn('You have <b>updates</b> for this widget!')}"
    >
      ${translateFn('You have <b>updates</b> for this widget!')}
    </gz-tooltip>
    <br />
    <br />
    <gz-tooltip
      isEllipsis
      class="four-line-tooltip"
      tooltipText="${translateFn(
        'To experience the latest design, close the current widget and select the new ones in the Settings panel. Apply your changes and save the view.'
      )}"
    >
      ${translateFn(
        'To experience the latest design, close the current widget and select the new ones in the Settings panel. Apply your changes and save the view.'
      )}
    </gz-tooltip>`;

  strings: EndpointEncryptionStringsSection = {
    items: [
      {
        key: 'compliantEndpoints',
        name: translateFn('Compliant with encryption policy'),
        chartColor: 'var(--green-teal-chart-300)',
      },
      {
        key: 'uncompliantEndpoints',
        name: translateFn('Non-compliant with encryption policy'),
        chartColor: 'var(--orange-alert-chart-100)',
      },
    ],
  };

  private renderBBChartTooltip([{ value, index }]): string {
    // tooltip used for the donut chart made with billboard.js library
    const legend = this.strings.items[index];

    return `
      <div class="chart-tooltip">
        <span class="name">${legend.name}</span>
        <span class="value">${value}</span>
      </div>
    `;
  }

  private emptyStateColor = 'var(--grey-layout-203-dark-theme-disabled-text)';

  firstUpdated(): void {
    super.firstUpdated();
    const { verticalPadding } = this.getPaddingConstants();
    this.verticalPadding = verticalPadding;

    this.updateChartVisibility();
  }

  private updateChartVisibility(): void {
    if (!this.chartContent || !this.parentElement) {
      return;
    }

    if (!this.isResized()) {
      removeElementClass(this.chartContent, 'hidden');
      return;
    }

    const availableView = this.parentElement.offsetHeight;
    const chartScale = parseInt(getComputedStyle(this.chartContent).getPropertyValue('--chart-scale'), 10);
    const contentHeight =
      this.getWidgetHeaderHeight(this.endpointEncryptionContainer) +
      chartScale +
      this.sectionContent.offsetHeight +
      this.verticalPadding * 2;

    if (
      availableView < contentHeight &&
      (this.state === WidgetStateFrontEnd.IS_INFO || this.state === WidgetStateFrontEnd.IS_CHANGED)
    ) {
      setElementClass(this.chartContent, 'hidden');
    } else {
      removeElementClass(this.chartContent, 'hidden');
    }
  }

  private buildChart = (): void => {
    const activeStateData = this.payload?.endpointsEncryption as HealthDashboardEndpointEncryptionData;
    const chartTitle = translateFn('endpoints');
    const compliantEndpoints = activeStateData?.overview?.compliantEndpoints ?? 0;
    const uncompliantEndpoints = activeStateData?.overview?.uncompliantEndpoints ?? 0;
    const chartValue = compliantEndpoints + uncompliantEndpoints;
    const displayChartValue = this.state === WidgetStateFrontEnd.IS_ACTIVE ? chartValue : `_ _`;
    const columns: number[] | number[][] =
      this.state !== WidgetStateFrontEnd.IS_ACTIVE
        ? [[0, 100]]
        : [
            [0, compliantEndpoints],
            [1, uncompliantEndpoints],
          ];

    const colors =
      this.state !== WidgetStateFrontEnd.IS_ACTIVE
        ? {
            [0]: this.emptyStateColor,
          }
        : {
            [0]: 'var(--green-teal-chart-300)',
            [1]: 'var(--orange-alert-chart-100)',
          };

    if (this.chartContent) this.chartContent.innerHTML = '';
    else return;

    bb.generate({
      data: {
        columns: columns,
        type: 'donut',
        colors: colors,
      },
      donut: {
        title: `${displayChartValue}\n${this.splitTitleIntoLines(chartTitle)}`,
        label: {
          show: false,
        },
        width: 7,
        padAngle: 0.04,
      },
      expand: false,
      tooltip: {
        show: true,
        contents: this.renderBBChartTooltip.bind(this),
        position: function(data, width, height, element, pos) {
          return {
            top: pos.y + 165,
            left: pos.x + 140,
          };
        }
      },
      legend: {
        show: false,
      },
      bindto: this.chartContent,
    });
  };

  private buildChartLegend = (): TemplateResult => {
    return html` <div id="chart-legend-list">${this.strings.items.map(item => this.renderItemCharLegend(item))}</div> `;
  };

  //used when translated strings contains more than one word
  private splitTitleIntoLines(title: string): string {
    const words = title.split(' ');
    const splitString = words.join('\n');
    return splitString;
  }

  private renderItemCharLegend(item: EndpointEncryptionItem): TemplateResult {
    const activeStateData = this.payload?.endpointsEncryption as HealthDashboardEndpointEncryptionData;
    const value = activeStateData?.hasOwnProperty('overview') ? activeStateData.overview[item.key] : 0;
    const itemName = this.state === WidgetStateFrontEnd.IS_ERROR ? '...' : item.name;
    const displayValue = this.state === WidgetStateFrontEnd.IS_ACTIVE ? value : html`_`;
    const bulletColor = this.state === WidgetStateFrontEnd.IS_ACTIVE ? item.chartColor : this.emptyStateColor;
    return html` <div class="chart-legend-item">
      <span class="chart-legend-bullet" style="background-color:${bulletColor}"></span>
      <div class="chart-legend fs-12">
        ${this.isResized()
          ? this.renderItemWithTooltip(itemName)
          : html`<span class="chart-legend-text">${itemName}</span>`}
        <span class="chart-legend-value">${displayValue}</span>
      </div>
    </div>`;
  }

  private renderItemWithTooltip(name: string): TemplateResult {
    return html`<gz-tooltip isEllipsis tooltipText="${name}">
      <span class="chart-legend-text">${name}</span>
    </gz-tooltip>`;
  }

  async updated(): Promise<void> {
    await this.updateComplete;
    if (this.chartContent) this.chartContent.innerHTML = '';

    this.buildChart();
    this.updateChartVisibility();
  }

  protected renderLoader(): TemplateResult {
    return html`
      <div class="${this.isResized() ? `loading-container-resized` : 'loading-container'}">
        <gz-loader class="flex-3"></gz-loader>
        <gz-loader class="flex-5"></gz-loader>
      </div>
    `;
  }

  protected handleResize(): void {
    super.handleResize();

    if (!this.parentElement) {
      return;
    }

    this.updateChartVisibility();
  }

  private isResized(): boolean {
    return !this.hasResizeLimits() && this.currentWidthInterval[1] === this.resizeBreakingPoints[0];
  }

  renderContent(): TemplateResult {
    return html`
      <div id="healthDashboardEndpointEncryption" class="${this.isResized() ? `resized` : nothing}">
        <div id="section-content">
          <div id="chart-content"></div>
          <div id="chart-legend-container">${this.renderInfoBox() ?? this.buildChartLegend()}</div>
        </div>
      </div>
    `;
  }

  protected renderInfoBox(): TemplateResult | undefined {
    if (this.state === WidgetStateFrontEnd.IS_CHANGED) {
      return this.renderChangedInfoBox(this.changedInfoBoxText);
    }
    const hasInheritedLicense = (this.payload?.endpointsEncryption as HealthDashboardInfoStateData)
      ?.hasInheritedLicense;
    let noLicenseText = translateFn(
      "No information available. To retrieve data about your company's {widgetName}, please consider a compatible {licenseLink} key.",
      {
        widgetName: '<b>' + getTranslatedWidgetTitle(WEB_COMPONENT_HEALTH_DASHBOARD_ENDPOINT_ENCRYPTION) + '</b>',
        licenseLink: `<gz-link type="underline" href="https://www.bitdefender.com/business/compare.html"><span>${translateFn(
          'license'
        )}</span></gz-link>`,
      }
    );

    if (hasInheritedLicense) {
      noLicenseText = translateFn(
        "To retrieve data about your company's {widgetName}, please contact your Bitdefender Partner and upgrade the licensing plan.",
        {
          widgetName: '<b>' + getTranslatedWidgetTitle(WEB_COMPONENT_HEALTH_DASHBOARD_ENDPOINT_ENCRYPTION) + '</b>',
        }
      );
    }
    return super.renderNoLicenseInfoBox(noLicenseText);
  }
}
